import { useQuery } from '@tanstack/react-query';
import { Table, Input } from 'antd';
import type {
  ColumnsType,
  ColumnType,
  FilterValue
} from 'antd/es/table/interface';
import { useMemo, useRef, useState } from 'react';
import { Attribute } from '../../../models/Attribute/Attribute';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Link } from 'react-router-dom';
import { FaEdit as EditIcon, FaTrash as DeleteIcon } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';

import style from './attribute-list.module.scss';
import axiosAPI from '../../../utils/client/axiosAPI';

type AttributeTableType = {
  id: number;
  name: string;
};

type DataIndex = keyof AttributeTableType;

export default function AttributeList() {
  const searchInput = useRef<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { data: attributes = [], refetch } = useQuery(['attributes'], () =>
    Attribute.all()
  );
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({});

  // Antd Table Search Props
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<AttributeTableType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div className="cs-table-dropdown" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
        />
        <div className="cs-table-dropdown-actions">
          <button onClick={() => confirm()} className="cs-table-button-primary">
            Pretraži
          </button>
          <button
            onClick={() => clearFilters && clearFilters()}
            className="cs-table-button-secondary"
          >
            Resetuj
          </button>
        </div>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined />,
    filteredValue: filteredInfo[dataIndex] || null,
    onFilter: (value, record) =>
      !!record[dataIndex] &&
      record[dataIndex]!.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    }
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosAPI.delete(`/admin/attributes/${deleteTarget.id}`);
      toast.success('Atribut uspešno obrisan.');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Greška pri brisanju atributa.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<AttributeTableType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Naziv',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name')
    },
    {
      key: 'action',
      title: 'Akcije',
      render: (_, attribute) => (
        <div className={style.actions}>
          <Link
            className="cs-edit-button"
            to={`/admin/attributes/edit/${attribute.id}`}
          >
            <EditIcon />
          </Link>
          <button
            className="cs-delete-button"
            onClick={() => {
              setDeleteTarget({ id: attribute.id, name: attribute.name });
              setShowDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </button>
        </div>
      )
    }
  ];

  const handleChange = (_pagination: any, filters: any) => {
    setFilteredInfo(filters);
  };

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title">Lista Atributa</h1>
      <div className={style.info}>
        <div className={style.number}>
          Trenutno imate <span>{attributes?.length}</span> atributa
        </div>
        <ButtonPrimary url="/admin/attributes/create">
          Dodaj Atribut
        </ButtonPrimary>
      </div>
      <Table
        columns={columns}
        dataSource={attributes}
        rowKey="id"
        className="cs-table-wrapper"
        onChange={handleChange}
        pagination={false}
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrda brisanja"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete atribut "${deleteTarget.name}"?`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Otkaži"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}
